import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo } from "react";
import { pdfjs } from "react-pdf";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";

import { datadogLogs } from "@datadog/browser-logs";
import Routes from "routes/routes";

import { store } from "app/store";
import { sunoTheme } from "app/themes";
import PreviousErrorBoundary, {
  ErrorBoundaryFallback as PreviousErrorBoundaryFallback
} from "components/error-boundary";
import NewErrorBoundaryFallback from "components/error-boundary/new-error-boundary-fallback.component";
import NewErrorBoundary from "components/error-boundary/new-error-boundary.component";
import {
  NEW_ERROR_SCREEN,
  SCHEDULING_BOOTSTRAP_TIMEOUT_MS
} from "constants/feature-flags";
import { MedicalScribeStreamingProvider } from "features/medical-scribe/use-medical-scribe-streaming.hook";
import TabSyncMessagesListener from "features/notifications/tab-sync-messages-listener.component";
import { useApiConfig } from "hooks/use-api-config.hook";
import { setCriticalTimeoutMs } from "utils/request-timeouts";

import AppSnackbar from "./features/app-layout/app-snackbar.component";

const setupPdfWorker = async () => {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf-worker/pdf.worker.min.js";
};

function App() {
  // Monitor feature flag and update API configuration dynamically
  useApiConfig();

  useEffect(() => {
    setupPdfWorker();
  }, []);
  useEffect(() => {
    const check = () => {
      if ((window as any).tinymceLoadError) {
        datadogLogs.logger.error("Failed to load /tinymce/tinymce.min.js");
      }
    };
    if (document.readyState === "complete") check();
    else window.addEventListener("load", check, { once: true });
  }, []);

  useEffect(() => {
    // Warm up tinymce-react module cache during idle time
    const idle =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 1);
    idle(() => {
      import("@tinymce/tinymce-react");
    });
  }, []);

  const flags = useFlags();
  const isNewErrorScreenEnabled = flags[NEW_ERROR_SCREEN] ?? false;

  // Override the armed default (see request-timeouts) only once LD has actually
  // resolved a value for this flag. During the SDK init window `flags[...]` is
  // undefined — we must NOT clobber the default to 0 then, or the highest-risk
  // cold-start /me fires unarmed. A resolved number (incl. 0 = kill-switch)
  // overrides; LD serving 0 disables the timeout.
  const schedulingBootstrapTimeoutMs = flags[SCHEDULING_BOOTSTRAP_TIMEOUT_MS];
  useEffect(() => {
    if (typeof schedulingBootstrapTimeoutMs === "number") {
      setCriticalTimeoutMs(schedulingBootstrapTimeoutMs);
    }
  }, [schedulingBootstrapTimeoutMs]);

  const ErrorBoundary = useMemo(() => {
    return isNewErrorScreenEnabled ? NewErrorBoundary : PreviousErrorBoundary;
  }, [isNewErrorScreenEnabled]);

  const ErrorBoundaryFallback = useMemo(() => {
    return isNewErrorScreenEnabled
      ? NewErrorBoundaryFallback
      : PreviousErrorBoundaryFallback;
  }, [isNewErrorScreenEnabled]);

  return (
    <ThemeProvider theme={sunoTheme}>
      <ErrorBoundary FallbackComponent={<ErrorBoundaryFallback />}>
        <Provider store={store}>
          <TabSyncMessagesListener />
          <BrowserRouter>
            <MedicalScribeStreamingProvider>
              <Routes />
              <AppSnackbar />
            </MedicalScribeStreamingProvider>
          </BrowserRouter>
        </Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
